import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { useExplanationFilterLines } from '../explanationFilters'
import { useLatest } from '../useLatest'
import { fetchOversikt, fetchPuls } from './api'
import type { OversiktResponse, PulsResponse } from './api'
import { fetchKlasserTable } from '../Klasser/api'
import type { KlasserListResponse } from '../Klasser/api'
import KpiCards from './KpiCards'
import MarkedspulsStrip from './MarkedspulsStrip'
import PeriodeGraf from './PeriodeGraf'
import Topplister from './Topplister'
import type { ComparisonMode } from './periodeComparison'
import KlassefordelingGrid from './KlassefordelingGrid'
import QueryExplanationBox from '../../../components/QueryExplanationBox'
import { useMaxDataDate } from '../../../hooks/useMaxDataDate'

export default function OversiktPage() {
  const { t } = useTranslation('bruktbil')
  const { from, to, periodExplicit, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer } = useBruktbilFilters()
  const [data, setData] = useState<OversiktResponse | null>(null)
  const [klasserData, setKlasserData] = useState<KlasserListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const maxDataDate = useMaxDataDate()
  const filterLines = useExplanationFilterLines()
  const [puls, setPuls] = useState<PulsResponse | null>(null)
  const [pulsLoading, setPulsLoading] = useState(false)
  const [pulsError, setPulsError] = useState<string | null>(null)
  const [compareMode, setCompareMode] = useState<ComparisonMode>('i_fjor')

  // The pulse follows the data, not the period: no from/to, no omregistrering.
  useLatest(alive => {
    setPulsLoading(true)
    setPulsError(null)
    fetchPuls({ klasse, varebil, selgerPostnummer, kjoperPostnummer })
      .then(r => { if (alive()) setPuls(r.data) })
      .catch(e => { if (alive()) setPulsError(e instanceof Error ? e.message : '') })
      .finally(() => { if (alive()) setPulsLoading(false) })
  }, [JSON.stringify(klasse), varebil, selgerPostnummer, kjoperPostnummer])

  useLatest(alive => {
    setLoading(true)
    setError(null)
    const filters = { from, to, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer }
    Promise.all([fetchOversikt(filters), fetchKlasserTable(filters)])
      .then(([oRes, kRes]) => {
        if (!alive()) return
        setData(oRes.data)
        setKlasserData(kRes.data)
      })
      // '' = no message to show; the fallback is translated at render.
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : '') })
      .finally(() => { if (alive()) setLoading(false) })
  }, [from, to, JSON.stringify(klasse), varebil, omregistrering, selgerPostnummer, kjoperPostnummer])

  if (error !== null) {
    return (
      <div className="card p-4 text-red-600 dark:text-red-400 text-sm">
        {t('oversikt.feilVedLasting', { error: error || t('oversikt.ukjentFeil') })}
      </div>
    )
  }

  const vehicleFilter = varebil === false ? 'personbil' : varebil === true ? 'varebil' : 'alle'

  return (
    <div className="space-y-4">
      <MarkedspulsStrip data={puls} loading={pulsLoading} error={pulsError} />
      <QueryExplanationBox
        maxDataDate={maxDataDate}
        // An explicitly chosen period is already exactly what the user asked
        // for, so show it verbatim; only the implicit default runs to today,
        // past where data goes, and needs clipping to the cutoff (#594 AC4).
        outputMode={periodExplicit ? 'custom' : 'year_to_date'}
        vehicleFilter={vehicleFilter as 'personbil' | 'varebil' | 'alle'}
        startDate={from}
        endDate={to}
        periodLabel={t('oversikt.valgtPeriode')}
        filterLines={filterLines}
      />
      <KpiCards data={loading ? null : (data?.kpis ?? null)} />
      <PeriodeGraf
        from={from}
        to={to}
        klasse={klasse}
        varebil={varebil}
        omregistrering={omregistrering}
        selgerPostnummer={selgerPostnummer}
        kjoperPostnummer={kjoperPostnummer}
        mode={compareMode}
        onModeChange={setCompareMode}
      />
      <Topplister
        from={from}
        to={to}
        klasse={klasse}
        varebil={varebil}
        omregistrering={omregistrering}
        selgerPostnummer={selgerPostnummer}
        kjoperPostnummer={kjoperPostnummer}
        mode={compareMode}
      />
      <KlassefordelingGrid rows={klasserData?.rows ?? []} />
    </div>
  )
}
